import { type ReactNode, createContext, useContext } from "react";
import { LocaleReconciler } from "#/components/i18n/LocaleReconciler.js";
import { MachineTranslationBanner } from "#/components/i18n/MachineTranslationBanner.js";
import type { TranslateFn } from "#/lib/i18n.js";
import { DEFAULT_LOCALE, type LocaleCode, isLocaleCode } from "#/lib/i18n/locales.js";
import { getActiveTemplate } from "#/lib/templates/index.js";
import { FooterBentoConsole } from "./chrome/FooterBentoConsole.js";
import { FooterEditorialLongform } from "./chrome/FooterEditorialLongform.js";
import { FooterNewsroom } from "./chrome/FooterNewsroom.js";
import { HeaderBentoConsole } from "./chrome/HeaderBentoConsole.js";
import { HeaderEditorialLongform } from "./chrome/HeaderEditorialLongform.js";
import { HeaderNewsroom } from "./chrome/HeaderNewsroom.js";

type PublicChromeRouterProps = {
  locale: string;
  t: TranslateFn;
  children: ReactNode;
};

const PublicChromeRouterContext = createContext(false);

export function PublicChromeRouter({ locale, t, children }: PublicChromeRouterProps) {
  const isNestedChrome = useContext(PublicChromeRouterContext);
  if (isNestedChrome) return <>{children}</>;

  const safeLocale: LocaleCode = isLocaleCode(locale) ? locale : DEFAULT_LOCALE;
  const template = getActiveTemplate();
  const Header =
    template.id === "bento-console"
      ? HeaderBentoConsole
      : template.id === "editorial-longform"
        ? HeaderEditorialLongform
        : HeaderNewsroom;
  const Footer =
    template.id === "bento-console"
      ? FooterBentoConsole
      : template.id === "editorial-longform"
        ? FooterEditorialLongform
        : FooterNewsroom;

  return (
    <PublicChromeRouterContext.Provider value={true}>
      <LocaleReconciler serverLocale={safeLocale} currentLogicalPath="/" />
      <MachineTranslationBanner locale={safeLocale} namespace="landing" t={t} />
      <Header locale={locale} t={t} />
      {children}
      <Footer locale={locale} t={t} />
    </PublicChromeRouterContext.Provider>
  );
}
